import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { LineChart } from '@gonfalon/dataviz-experimental/LineChart';
import { parsedQueryToAntlr, useQueryBuilderContext } from '@gonfalon/dataviz-experimental/QueryBuilder';
import { Series } from '@gonfalon/dataviz-experimental/Series';
import type { FieldOptions, ParsedToken } from '@gonfalon/dataviz-experimental/types';
import { XAxis } from '@gonfalon/dataviz-experimental/XAxis';
import { YAxis } from '@gonfalon/dataviz-experimental/YAxis';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { QueryChartDataRequestBody } from '@gonfalon/openapi/operations/queryChartData';
import { queryChartData } from '@gonfalon/openapi/operations/queryChartData';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { JsonSnippet } from '@gonfalon/ui/Snippet';
import {
  Button,
  ButtonGroup,
  Cell,
  Column,
  Dialog,
  DialogTrigger,
  Disclosure,
  DisclosurePanel,
  Focusable,
  Popover,
  ProgressBar,
  RadioButton,
  RadioGroup,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TagGroup,
  TagList,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';

import { buildDatavizQuery } from './buildDatavizQuery';
import {
  CHART_TIME_RANGES,
  type ChartTimeRangeKey,
  DEFAULT_CHART_TIME_RANGE_KEY,
  isChartTimeRangeKey,
  ROW_PAGE_SIZE,
} from './constants';
import { EnvironmentBadgeContent } from './EnvironmentBadgeContent';
import { type GroupedSpanRow, groupTraceRows } from './groupTraceRows';
import { useSampleTrace } from './useSampleTrace';

import styles from './TraceDataPreviewTable.module.css';

export function TraceDataPreviewTable({
  isEditable = true,
  submittedQuery,
  fieldOptions,
}: {
  isEditable?: boolean;
  submittedQuery?: ParsedToken[];
  /**
   * Trace field type metadata. Forwarded to `buildDatavizQuery` so it can
   * emit the boolean-specific OR-expansion for present-sentinel filters
   * (`field:=:__present__` on a boolean field → `field=false OR field=true`).
   */
  fieldOptions?: FieldOptions[];
}) {
  const projectKey = useProjectKey();
  const { parsedCommittedQuery, parsedQuery, dispatch, query } = useQueryBuilderContext();
  const queryForData = submittedQuery ?? parsedCommittedQuery;

  // Apply the picked value to a filter on `field`. If the parsed query already
  // contains a top-level FILTER on that field, mutate its value through the
  // reducer rather than appending a duplicate filter; otherwise splice a new
  // `field:=:value` term onto the end of the query string. Adjacent filters
  // are implicitly conjuncted by the grammar so no `AND` is inserted.
  //
  // Look up against the *live* `parsedQuery` (not `parsedCommittedQuery`): the
  // reducer's UPDATE_FILTER_VALUE splices into the live `query` using the token's
  // character offsets, and the append path below also builds on live `query`, so
  // the token offsets must come from the live parse or uncommitted edits would
  // shift them and corrupt the splice.
  function applyFilter(field: string, value: string) {
    const existing = parsedQuery.find(
      (t): t is Extract<ParsedToken, { type: 'FILTER' }> => t.type === 'FILTER' && t.field === field,
    );
    if (existing) {
      dispatch({ type: 'UPDATE_FILTER_VALUE', token: existing, value });
      return;
    }
    const needsQuotes = /[\s,:"()]/.test(value);
    const quoted = needsQuotes ? `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"` : value;
    const filterToken = `${field}:=:${quoted}`;
    const newQuery = query.trim() ? `${query.trim()} ${filterToken}` : filterToken;
    dispatch({ type: 'SET_QUERY', query: newQuery });
  }

  function addSpanNameFilter(spanName: string) {
    applyFilter('span_name', spanName);
  }

  function addEnvFilter(environment: string) {
    applyFilter('environment', environment);
  }
  // `wantsExpand` is the user's (or auto-expand's) *intent* to open the preview;
  // `isExpanded` is the actual disclosure-open state. They diverge while the
  // chart query is loading — the trigger shows a spinner-in-place-of-chevron and
  // is disabled, and the disclosure only opens once `data` arrives.
  const [isExpanded, setIsExpanded] = useState(false);
  const [wantsExpand, setWantsExpand] = useState(false);
  const [prevSubmittedQuery, setPrevSubmittedQuery] = useState(submittedQuery);
  // Once the user has manually closed the disclosure, treat further committed-
  // query changes as silent — don't reopen automatically. Re-armed the moment
  // the user opens the disclosure again. Note: we still kick off the fetch
  // below so the trigger's chevron-or-spinner reflects the in-flight refresh;
  // this flag only suppresses the *auto-expand* behavior, not the data load.
  const userCollapsedRef = useRef(false);
  const [now] = useState(() => Date.now());
  // Same wantsExpand / isExpanded split as the disclosure trigger above, scoped
  // to per-row span expansion: `wantsExpandSpanKey` drives the sample-trace fetch
  // and the per-row spinner state; `expandedSpanKey` is the actual displayed
  // expansion, only set once the trace JSON is loaded.
  const [expandedSpanKey, setExpandedSpanKey] = useState<string | null>(null);
  const [wantsExpandSpanKey, setWantsExpandSpanKey] = useState<string | null>(null);
  // Environment used to fetch the trace JSON sample for the expanded span.
  // Defaults to the span's top environment when null; reset when the user toggles between spans.
  const [selectedTraceEnvKey, setSelectedTraceEnvKey] = useState<string | null>(null);
  if (submittedQuery !== prevSubmittedQuery) {
    setPrevSubmittedQuery(submittedQuery);
    setWantsExpand(true);
    // Collapse any open row drilldown — the sample JSON we previously fetched
    // was for the *old* committed query's spans, so leaving it visible would
    // misrepresent the new result set. Resetting `wantsExpandSpanKey` (not
    // just `expandedSpanKey`) is what stops the auto-open path from
    // immediately re-opening the same row off cached placeholder data.
    setWantsExpandSpanKey(null);
    setExpandedSpanKey(null);
    setSelectedTraceEnvKey(null);
  }
  const [displayedRowCount, setDisplayedRowCount] = useState(ROW_PAGE_SIZE);
  const [prevGroupedRows, setPrevGroupedRows] = useState<GroupedSpanRow[] | null>(null);
  const [chartTimeRangeKey, setChartTimeRangeKey] = useState<ChartTimeRangeKey>(DEFAULT_CHART_TIME_RANGE_KEY);

  const toggleSpan = (spanName: string) => {
    if (wantsExpandSpanKey === spanName) {
      setWantsExpandSpanKey(null);
      setExpandedSpanKey(null);
      setSelectedTraceEnvKey(null);
      return;
    }
    // Switching to a different row — leave `expandedSpanKey` pointing at the
    // currently-open row so its JSON panel stays visible while the new fetch
    // is in flight. The `useEffect` below flips `expandedSpanKey` to the new
    // span once `isPlaceholderData` flips false (real data arrived), giving
    // an atomic swap rather than a transient "nothing open" state.
    setWantsExpandSpanKey(spanName);
    setSelectedTraceEnvKey(null);
  };

  const timeRange = useMemo(() => {
    const { durationMs, interval } = CHART_TIME_RANGES[chartTimeRangeKey];
    return { from: now - durationMs, to: now, interval };
  }, [now, chartTimeRangeKey]);

  const filters = useMemo(
    () =>
      buildDatavizQuery(
        queryForData,
        {
          projectKey,
          environmentKey: 'production',
          dataSource: {
            domain: 'observability',
            kind: 'traces',
            timeRange: { ...timeRange, timezone: 'UTC' },
          },
          select: [],
          groupBy: [],
        },
        fieldOptions,
      ).filters,
    [projectKey, queryForData, timeRange, fieldOptions],
  );

  const { data, isLoading, isFetching } = useQuery({
    // Fire as soon as the user signals intent (`wantsExpand`) so the spinner
    // on the trigger reflects an in-flight request before the disclosure opens.
    // Keep firing while open so `submittedQuery` / time-range changes refetch.
    enabled: wantsExpand || isExpanded,
    queryKey: ['chartData', 'preview', projectKey, filters, timeRange],
    queryFn: async () =>
      queryChartData({
        body: {
          projectKey,
          dataSource: { domain: 'observability', kind: 'traces', timeRange },
          // FiltersClause uses the QueryBuilder's wider Operator alphabet (**, //, !//,
          // startsWith, endsWith) that the chart-data endpoint accepts at runtime but the
          // generated FilterOp enum doesn't yet enumerate. Narrow the typing concession to
          // this one field rather than the whole body.
          filters: filters as unknown as QueryChartDataRequestBody['filters'],
          // The traces schema registers a `duration:count` expression that emits `count(*)`
          // (see event-recorder MetricValueFieldExpressions). Without a select, the API only
          // returns timestamp buckets and the sparklines collapse to a flat line.
          select: [{ field: 'duration', expression: 'count' }],
          groupBy: [{ field: 'span_name' }, { field: 'environment' }],
        },
      }),
    // Keep the previous response visible while refetching (e.g. when the time-range
    // radio is toggled) so the table doesn't flash to its empty state — the
    // refetch overlay below dims the stale rows until the new ones arrive.
    placeholderData: keepPreviousData,
  });

  // True only when a refetch is in flight on top of already-rendered data —
  // matches the MetricDataSourceEditorFields "results preview" overlay pattern.
  const isRefetching = isFetching && !isLoading;

  // Trigger should show a spinner in place of the chevron-right whenever the
  // preview is closed and a chart fetch is in flight — whether it's the
  // initial load before opening or a background refetch that hasn't finished
  // yet. Uses `isFetching` (not `isLoading`) so we light up the spinner on
  // every fetch, not just the first-ever one without cached data.
  const isPendingExpand = !isExpanded && isFetching;

  // Open the disclosure as soon as the data we asked for is available. Covers
  // both the click-to-expand path and the auto-expand-on-submittedQuery-change
  // useEffect above; in the cached-data case `data` is already truthy when
  // `wantsExpand` flips so the disclosure opens on the very same render.
  // Gated on `!userCollapsedRef.current` so a manually-dismissed preview never
  // reopens itself when a background refresh completes — the trigger still
  // surfaces the in-flight loader, the panel just stays closed.
  useEffect(() => {
    if (wantsExpand && data && !isExpanded && !userCollapsedRef.current) {
      setIsExpanded(true);
    }
  }, [wantsExpand, data, isExpanded]);

  const handleExpandChange = (next: boolean) => {
    setWantsExpand(next);
    if (!next) {
      setIsExpanded(false);
      userCollapsedRef.current = true;
    } else {
      userCollapsedRef.current = false;
      if (data) {
        setIsExpanded(true);
      }
    }
  };

  const antlrQuery = useMemo(() => parsedQueryToAntlr(queryForData), [queryForData]);

  const fields = data?.data?.fields ?? [];
  const groupByFields = data?.data?.group_by_fields ?? [];
  const items = data?.data?.items;

  const timestampIdx = fields.findIndex((f) => f.name === 'timestamp');
  // Column name = `${field}_${expression}` per TracesFieldExpression.ColumnName().
  const countIdx = fields.findIndex((f) => f.name === 'duration_count');
  const spanNameIdx = groupByFields.findIndex((f) => f.name === 'span_name');
  const environmentIdx = groupByFields.findIndex((f) => f.name === 'environment');

  const groupedRows = useMemo(
    () => groupTraceRows(items ?? [], { spanNameIdx, environmentIdx, timestampIdx, countIdx }),
    [items, spanNameIdx, environmentIdx, timestampIdx, countIdx],
  );

  if (groupedRows !== prevGroupedRows) {
    setPrevGroupedRows(groupedRows);
    if (prevGroupedRows !== null) {
      setDisplayedRowCount(ROW_PAGE_SIZE);
    }
  }

  const visibleRows = groupedRows.slice(0, displayedRowCount);
  const hasMoreRows = groupedRows.length > displayedRowCount;

  // Falls back to the expanded span's top environment so the trace fetch has a sensible default
  // before the user picks an env card. Driven by `wantsExpandSpanKey` (not
  // `expandedSpanKey`) so the row's env / fetch are derivable while the row is
  // pending-but-not-yet-open.
  const pendingOrExpandedSpanKey = wantsExpandSpanKey ?? expandedSpanKey;
  const expandedSpanRow = groupedRows.find((r) => r.spanName === pendingOrExpandedSpanKey) ?? null;
  const effectiveTraceEnvKey = selectedTraceEnvKey ?? expandedSpanRow?.environments[0]?.environment ?? null;

  const {
    data: expandedSpanTrace,
    isLoading: isSpanTraceLoading,
    isFetching: isSpanTraceFetching,
    isPlaceholderData: isSpanTracePlaceholder,
  } = useSampleTrace({
    enabled: isExpanded && wantsExpandSpanKey !== null,
    antlrQuery,
    spanName: wantsExpandSpanKey,
    environmentKey: effectiveTraceEnvKey,
  });

  // Only true when react-query is serving the previous query's data as
  // `placeholderData` while a new queryKey is fetching — i.e. the env-card
  // switch case where we deliberately want to dim the now-stale JSON.
  // Using `isFetching && !isLoading` instead would also fire on the
  // stale-while-revalidate background refetch that runs when a row is
  // re-opened with cache data for the same queryKey, briefly flashing the
  // overlay over fresh-and-correct JSON.
  const isSpanTraceRefetching = isSpanTracePlaceholder;

  // Open the row once its trace fetch *settles* — same wantsExpand → flip
  // pattern as the disclosure-level effect higher up. Two guards, each
  // load-bearing: `!isSpanTraceLoading` holds the first open until its initial
  // fetch finishes (there's no placeholder yet, so isPlaceholderData is already
  // false); `!isSpanTracePlaceholder` holds an already-open row in place across
  // a row-switch, since `useSampleTrace` uses `placeholderData: keepPreviousData`
  // and serves the previous span's data (isPlaceholderData = true) until the new
  // span resolves. Crucially we do NOT gate on `expandedSpanTrace` being truthy —
  // a span whose sample comes back empty must still open its row, otherwise the
  // previously-expanded row stays open showing a stale/blank snippet.
  if (wantsExpandSpanKey && !isSpanTraceLoading && !isSpanTracePlaceholder && expandedSpanKey !== wantsExpandSpanKey) {
    setExpandedSpanKey(wantsExpandSpanKey);
  }

  return (
    <div className={styles.previewWrapper}>
      <Disclosure isExpanded={isExpanded} onExpandedChange={handleExpandChange}>
        {({ isExpanded: disclosureExpanded }) => (
          <>
            <div className={styles.disclosureHeader}>
              <Button slot="trigger" variant="minimal" className={styles.previewButton} isDisabled={isPendingExpand}>
                <span className={styles.disclosureIcon}>
                  {isPendingExpand ? (
                    <ProgressBar size="small" isIndeterminate aria-label={msg('metrics.spanPreview.loadingLabel')} />
                  ) : (
                    <Icon
                      name={disclosureExpanded ? 'chevron-down' : 'chevron-right'}
                      size="small"
                      aria-label={
                        disclosureExpanded
                          ? msg('metrics.spanPreview.collapseLabel')
                          : msg('metrics.spanPreview.expandLabel')
                      }
                    />
                  )}
                </span>
                <span className={styles.disclosureLabel}>
                  <Message id="metrics.spanPreview.previewMatchingSpans" />
                  <TooltipTrigger>
                    <Focusable>
                      <TagGroup aria-label={msg('metrics.spanPreview.featureStatusLabel')} className={styles.badge}>
                        <TagList>
                          <Tag size="small" variant="beta">
                            {msg('metrics.spanPreview.beta')}
                          </Tag>
                        </TagList>
                      </TagGroup>
                    </Focusable>
                    <Tooltip>
                      <Message id="metrics.spanPreview.disclaimer" />
                    </Tooltip>
                  </TooltipTrigger>
                </span>
              </Button>
              {disclosureExpanded && (
                <DialogTrigger>
                  <TooltipTrigger>
                    <Button className={styles.intervalSelectorTrigger} variant="minimal">
                      <Icon name="clock" slot="start-icon" />
                      {chartTimeRangeKey}
                    </Button>
                    <Tooltip>
                      <Message id="metrics.spanPreview.changeTimeRange" />
                    </Tooltip>
                  </TooltipTrigger>
                  <Popover className={styles.intervalPopover} placement="bottom end">
                    <Dialog>
                      <RadioGroup
                        aria-label={msg('metrics.spanPreview.timeRangeLabel')}
                        value={chartTimeRangeKey}
                        onChange={(value) => {
                          if (isChartTimeRangeKey(value)) {
                            setChartTimeRangeKey(value);
                          }
                        }}
                      >
                        <ButtonGroup role="presentation" spacing="compact" orientation="vertical">
                          <RadioButton value="1h">1h</RadioButton>
                          <RadioButton value="24h">24h</RadioButton>
                          <RadioButton value="7d">7d</RadioButton>
                        </ButtonGroup>
                      </RadioGroup>
                    </Dialog>
                  </Popover>
                </DialogTrigger>
              )}
            </div>
            <DisclosurePanel className={styles.disclosurePanel}>
              {!isLoading && groupedRows.length === 0 ? (
                <div className={styles.noDataFound}>
                  <span>
                    <Message id="metrics.spanPreview.noTraceData" />
                  </span>
                </div>
              ) : (
                <div className={clsx(styles.tableScrollContainer, { [styles.isRefetching]: isRefetching })}>
                  {isRefetching && (
                    <div className={styles.refetchOverlay}>
                      <ProgressBar isIndeterminate aria-label={msg('metrics.spanPreview.loadingLabel')} />
                    </div>
                  )}
                  <Table className={styles.tracePreviewTable}>
                    <TableHeader>
                      <Column key="expand" isRowHeader className={styles.expandColumn} />
                      <Column key="name" isRowHeader className={styles.nameHeader}>
                        <Message id="metrics.spanPreview.span" />
                      </Column>
                      <Column key="environments" isRowHeader className={styles.environmentsHeader}>
                        <Message id="metrics.spanPreview.environments" />
                      </Column>
                      <Column key="total" isRowHeader className={styles.totalHeader}>
                        <Message id="metrics.spanPreview.total" />
                      </Column>
                      <Column key="empty" isRowHeader className={styles.emptyCell} />
                    </TableHeader>
                    <TableBody>
                      {visibleRows.map(({ spanName, environments, totalCount }) => {
                        const isSpanExpanded = expandedSpanKey === spanName;
                        const isSpanPendingExpand =
                          wantsExpandSpanKey === spanName && !isSpanExpanded && isSpanTraceFetching;
                        // Only dim/overlay the JSON for the row that's *currently open*
                        // AND whose data is the one being fetched. A row-switch (different
                        // `wantsExpandSpanKey` than `expandedSpanKey`) keeps the previously-
                        // open row visible without overlay until the new row swaps in — the
                        // pending row's trigger spinner already signals the load.
                        const isThisRowRefetching =
                          isSpanExpanded && wantsExpandSpanKey === spanName && isSpanTraceRefetching;
                        return (
                          <Fragment key={spanName}>
                            <Row className={styles.tableRow}>
                              <Cell
                                onClick={isSpanPendingExpand ? undefined : () => toggleSpan(spanName)}
                                className={styles.expandCellContainer}
                              >
                                <TooltipTrigger>
                                  <Focusable>
                                    <div className={styles.expandCellContent}>
                                      {isSpanPendingExpand ? (
                                        <ProgressBar
                                          size="small"
                                          isIndeterminate
                                          aria-label={msg('metrics.spanPreview.loadingSampleForLabel', { spanName })}
                                        />
                                      ) : (
                                        <Icon
                                          name="chevron-right"
                                          size="small"
                                          className={`${styles.expandIcon} ${isSpanExpanded ? styles.expanded : ''}`}
                                        />
                                      )}
                                    </div>
                                  </Focusable>
                                  <Tooltip>
                                    <Message id="metrics.spanPreview.expandSpanSample" />
                                  </Tooltip>
                                </TooltipTrigger>
                              </Cell>
                              <Cell className={styles.nameCell}>
                                {isEditable ? (
                                  <TooltipTrigger>
                                    <Focusable>
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        data-interactive
                                        className={styles.spanNameLink}
                                        onClick={() => addSpanNameFilter(spanName)}
                                        onKeyDown={(e) => e.key === 'Enter' && addSpanNameFilter(spanName)}
                                      >
                                        {spanName}
                                      </span>
                                    </Focusable>
                                    <Tooltip>
                                      <Message id="metrics.spanPreview.filterToSpan" />
                                    </Tooltip>
                                  </TooltipTrigger>
                                ) : (
                                  <span className={styles.spanNameLink}>{spanName}</span>
                                )}
                              </Cell>
                              <Cell className={styles.environmentsCell}>
                                <div className={styles.envBadgeList}>
                                  {environments[0] &&
                                    (isEditable ? (
                                      <TooltipTrigger>
                                        <Focusable>
                                          <span
                                            role="button"
                                            tabIndex={0}
                                            data-interactive
                                            className={styles.envBadge}
                                            onClick={() => addEnvFilter(environments[0].environment)}
                                            onKeyDown={(e) =>
                                              e.key === 'Enter' && addEnvFilter(environments[0].environment)
                                            }
                                          >
                                            <EnvironmentBadgeContent
                                              projectKey={projectKey}
                                              environmentKey={environments[0].environment}
                                            />
                                          </span>
                                        </Focusable>
                                        <Tooltip>
                                          <Message id="metrics.spanPreview.filterToEnvironment" />
                                        </Tooltip>
                                      </TooltipTrigger>
                                    ) : (
                                      <span className={styles.envBadge}>
                                        <EnvironmentBadgeContent
                                          projectKey={projectKey}
                                          environmentKey={environments[0].environment}
                                        />
                                      </span>
                                    ))}
                                  {environments.length > 1 && (
                                    <DialogTrigger>
                                      <Button size="small" variant="minimal" className={styles.envBadgeMoreButton}>
                                        +{environments.length - 1}
                                      </Button>
                                      <Popover className={styles.envBadgePopover}>
                                        <div className={styles.envBadgePopoverList}>
                                          {environments.slice(1).map(({ environment }) =>
                                            isEditable ? (
                                              <TooltipTrigger key={environment}>
                                                <Focusable>
                                                  <span
                                                    role="button"
                                                    tabIndex={0}
                                                    data-interactive
                                                    className={styles.envBadge}
                                                    onClick={() => addEnvFilter(environment)}
                                                    onKeyDown={(e) => e.key === 'Enter' && addEnvFilter(environment)}
                                                  >
                                                    <EnvironmentBadgeContent
                                                      projectKey={projectKey}
                                                      environmentKey={environment}
                                                    />
                                                  </span>
                                                </Focusable>
                                                <Tooltip>
                                                  <Message id="metrics.spanPreview.filterToEnvironment" />
                                                </Tooltip>
                                              </TooltipTrigger>
                                            ) : (
                                              <span key={environment} className={styles.envBadge}>
                                                <EnvironmentBadgeContent
                                                  projectKey={projectKey}
                                                  environmentKey={environment}
                                                />
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      </Popover>
                                    </DialogTrigger>
                                  )}
                                </div>
                              </Cell>
                              <Cell className={styles.totalCell}>{totalCount.toLocaleString()}</Cell>
                              <Cell className={styles.emptyCell} />
                            </Row>
                            <Row className={`${styles.expandedRowContainer} ${isSpanExpanded ? styles.expanded : ''}`}>
                              <Cell
                                colSpan={5}
                                className={`${styles.expandedRow} ${isSpanExpanded ? styles.expanded : ''}`}
                              >
                                <div className={styles.expandedRowContent}>
                                  {isSpanExpanded && (
                                    <>
                                      <div className={styles.envCards}>
                                        {environments.map(({ environment, sparklineData: envSparkline }) => {
                                          const isSelected = effectiveTraceEnvKey === environment;
                                          const isSoleEnvironment = environments.length === 1;
                                          const cardContent = (
                                            <>
                                              <span className={styles.envCardName}>
                                                <EnvironmentBadgeContent
                                                  projectKey={projectKey}
                                                  environmentKey={environment}
                                                />
                                              </span>
                                              <LineChart data={envSparkline} height={90} disableDragToZoom>
                                                <XAxis dataKey="timestamp" dataType="time" maxNumTicks={4} />
                                                <YAxis dataType="number" numTicks={3} />
                                                <Series
                                                  seriesKey="count"
                                                  name={msg('metrics.spanPreview.spanCountSeries')}
                                                  dataKey="count"
                                                  strokeWidth={1.5}
                                                />
                                              </LineChart>
                                            </>
                                          );
                                          if (isSoleEnvironment) {
                                            return (
                                              <div key={environment} className={styles.envCardSolo}>
                                                {cardContent}
                                              </div>
                                            );
                                          }
                                          return (
                                            <div
                                              key={environment}
                                              className={`${styles.envCard} ${isSelected ? styles.envCardSelected : ''}`}
                                              role="button"
                                              tabIndex={0}
                                              aria-pressed={isSelected}
                                              onClick={() => setSelectedTraceEnvKey(environment)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  setSelectedTraceEnvKey(environment);
                                                }
                                              }}
                                            >
                                              {cardContent}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      {isSpanTraceLoading ? (
                                        <div className={styles.loader}>
                                          <ProgressBar
                                            size="small"
                                            isIndeterminate
                                            aria-label={msg('metrics.spanPreview.loadingSampleLabel')}
                                          />
                                        </div>
                                      ) : expandedSpanTrace &&
                                        (isSpanTracePlaceholder || wantsExpandSpanKey === spanName) ? (
                                        // Only render `expandedSpanTrace` here when it actually
                                        // belongs to this row: placeholder data is the previous
                                        // result still shown in the open row, and fresh data
                                        // belongs to `wantsExpandSpanKey`. Without the
                                        // `wantsExpandSpanKey === spanName` guard, fresh JSON for a
                                        // just-clicked span flashes under the still-open prior row
                                        // (which has a lagging `expandedSpanKey`) — most visibly
                                        // when the new span is already cached and resolves with no
                                        // placeholder frame.
                                        //
                                        // When a new fetch is in flight on top of already-shown
                                        // JSON (e.g. the user clicked a different env card),
                                        // dim the stale snippet via `.isRefetching::after` and
                                        // center the same `refetchOverlay` spinner the table
                                        // uses, so the loading state mirrors the table pattern.
                                        <div
                                          className={clsx(styles.jsonSnippetWrapper, {
                                            [styles.isRefetching]: isThisRowRefetching,
                                          })}
                                        >
                                          {isThisRowRefetching && (
                                            <div className={styles.refetchOverlay}>
                                              <ProgressBar
                                                isIndeterminate
                                                aria-label={msg('metrics.spanPreview.loadingSampleLabel')}
                                              />
                                            </div>
                                          )}
                                          <JsonSnippet
                                            object={expandedSpanTrace}
                                            withCopyButton
                                            className={styles.jsonSnippet}
                                          />
                                        </div>
                                      ) : wantsExpandSpanKey === spanName ? (
                                        // Fetch settled for this row with no sample trace. Show an
                                        // explicit empty state rather than a blank gap.
                                        <div className={styles.noDataFound}>
                                          <span>
                                            <Message id="metrics.spanPreview.noSampleTrace" />
                                          </span>
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                              </Cell>
                            </Row>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {hasMoreRows && (
                    <div className={styles.loadMoreContainer}>
                      <Button variant="minimal" onPress={() => setDisplayedRowCount((c) => c + ROW_PAGE_SIZE)}>
                        {msg('metrics.spanPreview.loadMore')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  );
}
